import {
  DIFICULTADES,
  INFORMANTES,
  LUGARES_INICIO,
  MODOS,
  MONEDAS,
  NIVELES,
  PARENTESCOS,
  RANGOS_DURACION,
  TARIFA_ESTUDIANTIL,
  type Opcion,
} from './catalogos.js'

/**
 * Definición declarativa del formulario (versión 1). La API la sirve como
 * JSON; la PWA y la app la usan para textos, orden y visibilidad. Las
 * reglas de validación viven en `validaciones.ts` (misma fuente de verdad).
 *
 * `ref` es el número de la pregunta en la propuesta original del cliente.
 */

export type TipoCampo =
  | 'texto'
  | 'entero'
  | 'opcion'
  | 'multiple'
  | 'hora'
  | 'monto'
  | 'geo_cascada'
  | 'institucion'
  | 'origen'
  | 'tramos'
  | 'si_no'
  | 'consentimiento'
  | 'casilla'

/** Condición simple: el campo `campo` tiene alguno de los valores `en`. */
export interface Condicion {
  campo: string
  en?: readonly (string | number | boolean)[]
  /** Condición numérica: el valor es menor que. */
  menorQue?: number
  /** Todas deben cumplirse. */
  y?: readonly Condicion[]
}

export interface Campo {
  id: string
  ref?: number
  tipo: TipoCampo
  etiqueta: string
  ayuda?: string
  /** true = siempre; Condicion = solo cuando se cumple. */
  obligatorio: boolean | Condicion
  visibleSi?: Condicion
  opciones?: readonly Opcion[]
  min?: number
  max?: number
  maxLargo?: number
  /** Solo para validar coherencia; se puede apagar por campaña. */
  validacion?: boolean
  soloAsistida?: boolean
}

export interface Paso {
  id: string
  titulo: string
  descripcion?: string
  campos: readonly Campo[]
}

export interface EsquemaFormulario {
  version: string
  titulo: string
  objetivo: string
  consentimiento: { version: string; texto: string; textoAsentimiento: string }
  pasos: readonly Paso[]
}

export const OBJETIVO =
  'Esta encuesta tiene como propósito recopilar información sobre los desplazamientos que realizan los ' +
  'estudiantes entre su lugar de origen y la institución educativa a la que asisten, con el fin de conocer ' +
  'sus patrones de movilidad y gastos asociados al transporte. La información será utilizada con fines de ' +
  'análisis y desarrollo de mejoras en el servicio de transporte para los estudiantes. Las respuestas serán ' +
  'tratadas de manera confidencial.'

const TEXTO_CONSENTIMIENTO =
  'Participar es voluntario y puede dejar de responder en cualquier momento. No pedimos su dirección exacta: ' +
  'el lugar de origen se guarda como sector o zona aproximada (unos 500 metros). El nombre es opcional y se ' +
  'guarda cifrado, separado de las respuestas. Los resultados se publican solo de forma agregada, sin ' +
  'identificar a ninguna persona. Los datos se conservan únicamente durante el tiempo que establece la ' +
  'política de retención del Ministerio y luego se anonimizan.'

const TEXTO_ASENTIMIENTO =
  'Queremos saber cómo llegas a tu escuela o liceo para mejorar el transporte estudiantil. No te preguntamos ' +
  'dónde vives exactamente. Si no quieres responder, no pasa nada.'

export const ESQUEMA_V1: EsquemaFormulario = {
  version: '1.0',
  titulo: 'Encuesta sobre movilidad y gastos de transporte de los estudiantes',
  objetivo: OBJETIVO,
  consentimiento: { version: '1.0', texto: TEXTO_CONSENTIMIENTO, textoAsentimiento: TEXTO_ASENTIMIENTO },
  pasos: [
    {
      id: 'inicio',
      titulo: 'Antes de empezar',
      campos: [
        { id: 'idOrganismo', ref: 1, tipo: 'texto', etiqueta: 'ID asignado por el organismo', obligatorio: true, maxLargo: 30, soloAsistida: true },
        { id: 'consentimiento', tipo: 'consentimiento', etiqueta: 'Acepto participar en la encuesta', obligatorio: true },
        { id: 'informante', tipo: 'opcion', etiqueta: '¿Quién responde la encuesta?', obligatorio: true, opciones: INFORMANTES,
          ayuda: 'En educación inicial y primaria responde el representante.' },
        { id: 'parentesco', tipo: 'opcion', etiqueta: 'Parentesco con el estudiante', opciones: PARENTESCOS,
          obligatorio: { campo: 'informante', en: ['representante'] }, visibleSi: { campo: 'informante', en: ['representante'] } },
      ],
    },
    {
      id: 'estudiante',
      titulo: 'Estudiante e institución',
      campos: [
        { id: 'nombre', ref: 2, tipo: 'texto', etiqueta: 'Nombre del estudiante', ayuda: 'Opcional. No es obligatorio.', obligatorio: false, maxLargo: 80 },
        { id: 'edad', ref: 3, tipo: 'entero', etiqueta: 'Edad (años cumplidos)', obligatorio: true, min: 2, max: 99 },
        { id: 'asentimiento', tipo: 'casilla', etiqueta: 'Quiero participar en la encuesta', obligatorio: true,
          visibleSi: { campo: 'informante', en: ['estudiante'], y: [{ campo: 'edad', menorQue: 18 }] } },
        { id: 'nivel', ref: 4, tipo: 'opcion', etiqueta: 'Nivel educativo que cursa', obligatorio: true, opciones: NIVELES },
        { id: 'nivelOtro', ref: 4, tipo: 'texto', etiqueta: 'Especifique el nivel', maxLargo: 60,
          obligatorio: { campo: 'nivel', en: ['otro'] }, visibleSi: { campo: 'nivel', en: ['otro'] } },
        { id: 'grado', tipo: 'texto', etiqueta: 'Grado, año o semestre', obligatorio: false, maxLargo: 20 },
        { id: 'institucionUbicacion', ref: 6, tipo: 'geo_cascada', etiqueta: 'Ubicación de la institución educativa',
          ayuda: 'Estado, municipio y parroquia', obligatorio: true },
        { id: 'institucionId', ref: 5, tipo: 'institucion', etiqueta: 'Nombre de la institución educativa',
          ayuda: 'Busque por nombre o código DEA / MPPEU. Si no aparece, puede agregarla.', obligatorio: true },
      ],
    },
    {
      id: 'origen',
      titulo: 'Origen del viaje',
      descripcion: 'No le pedimos su dirección exacta, solo el sector o zona.',
      campos: [
        { id: 'lugarInicio', ref: 7, tipo: 'opcion', etiqueta: '¿Dónde inicia habitualmente su desplazamiento hacia la institución educativa?',
          obligatorio: true, opciones: LUGARES_INICIO },
        { id: 'lugarInicioOtro', ref: 7, tipo: 'texto', etiqueta: 'Especifique el lugar', maxLargo: 60,
          obligatorio: { campo: 'lugarInicio', en: ['otro'] }, visibleSi: { campo: 'lugarInicio', en: ['otro'] } },
        { id: 'origen', ref: 8, tipo: 'origen', obligatorio: true,
          etiqueta: 'Ciudad o localidad, y sector, urbanización, barrio o zona desde donde inicia el viaje',
          ayuda: 'Marque en el mapa o elija su sector. Guardamos solo la zona aproximada (unos 500 m).' },
      ],
    },
    {
      id: 'ida',
      titulo: 'Viaje de ida',
      campos: [
        { id: 'diasSemana', ref: 10, tipo: 'entero', etiqueta: '¿Cuántos días a la semana se desplaza habitualmente hacia la institución educativa?',
          obligatorio: true, min: 1, max: 7 },
        { id: 'horaSalidaIda', ref: 11, tipo: 'hora', etiqueta: '¿A qué hora sale habitualmente de su lugar de origen para dirigirse a la institución educativa?', obligatorio: true },
        { id: 'horaLlegadaIda', ref: 13, tipo: 'hora', etiqueta: '¿A qué hora llega habitualmente a la institución educativa?', obligatorio: true },
        { id: 'declarados.rangoIda', ref: 12, tipo: 'opcion', validacion: true, obligatorio: false, opciones: RANGOS_DURACION,
          etiqueta: '¿Cuánto tiempo tarda normalmente en llegar desde su lugar de origen hasta la institución educativa?' },
        { id: 'modoIda', ref: 14, tipo: 'opcion', obligatorio: true, opciones: MODOS,
          etiqueta: '¿Cuál es el principal modo de transporte que utiliza para desplazarse desde su lugar de origen hasta la institución educativa?',
          ayuda: 'El transporte en el que pasa más tiempo.' },
        { id: 'modoIdaOtro', ref: 14, tipo: 'texto', etiqueta: 'Especifique el modo', maxLargo: 60,
          obligatorio: { campo: 'modoIda', en: ['otro'] }, visibleSi: { campo: 'modoIda', en: ['otro'] } },
        { id: 'tramos.ida', tipo: 'tramos', obligatorio: false, etiqueta: '¿Usa más de un transporte para llegar?',
          ayuda: 'Opcional: agregue cada tramo con su tiempo y costo.' },
      ],
    },
    {
      id: 'regreso',
      titulo: 'Viaje de regreso',
      campos: [
        { id: 'regresoIgualIda', tipo: 'si_no', obligatorio: false, etiqueta: '¿El regreso es igual que la ida (mismo transporte)?' },
        { id: 'horaSalidaRegreso', ref: 16, tipo: 'hora', obligatorio: true,
          etiqueta: '¿A qué hora inicia habitualmente el viaje de regreso desde la institución educativa?' },
        { id: 'horaLlegadaRegreso', tipo: 'hora', obligatorio: true, etiqueta: '¿A qué hora llega de vuelta a su lugar de origen?' },
        { id: 'declarados.rangoRegreso', ref: 15, tipo: 'opcion', validacion: true, obligatorio: false, opciones: RANGOS_DURACION,
          etiqueta: '¿Cuánto tiempo tarda normalmente en llegar desde la institución educativa hasta su lugar de origen?' },
        { id: 'modoRegreso', ref: 17, tipo: 'opcion', obligatorio: true, opciones: MODOS,
          etiqueta: '¿Cuál es el principal modo de transporte que utiliza para regresar de la institución educativa a su lugar de origen?',
          visibleSi: { campo: 'regresoIgualIda', en: [false] } },
        { id: 'modoRegresoOtro', ref: 17, tipo: 'texto', etiqueta: 'Especifique el modo', maxLargo: 60,
          obligatorio: { campo: 'modoRegreso', en: ['otro'] }, visibleSi: { campo: 'modoRegreso', en: ['otro'] } },
        { id: 'tramos.regreso', tipo: 'tramos', obligatorio: false, etiqueta: '¿Usa más de un transporte para regresar?',
          visibleSi: { campo: 'regresoIgualIda', en: [false] } },
      ],
    },
    {
      id: 'gasto',
      titulo: 'Gasto en transporte',
      descripcion: 'Indique el monto y la moneda. Lo convertimos a dólares con la tasa oficial del BCV del día.',
      campos: [
        { id: 'gastoIda', ref: 18, tipo: 'monto', obligatorio: true, opciones: MONEDAS,
          etiqueta: '¿Cuánto dinero gasta habitualmente en transporte para llegar a la institución educativa en un día de asistencia?',
          ayuda: 'Si no gasta nada, escriba 0.' },
        { id: 'gastoRegreso', ref: 19, tipo: 'monto', obligatorio: true, opciones: MONEDAS,
          etiqueta: '¿Cuánto gasta habitualmente en transporte para regresar de la institución educativa a su lugar de origen?' },
        { id: 'declarados.gastoMensual', ref: 20, tipo: 'monto', validacion: true, obligatorio: false, opciones: MONEDAS,
          etiqueta: '¿Cuánto gasta aproximadamente en transporte durante un mes?' },
        { id: 'declarados.diasMes', ref: 21, tipo: 'entero', validacion: true, obligatorio: false, min: 1, max: 31,
          etiqueta: 'Aproximadamente, ¿cuántos días al mes se desplaza desde su lugar de origen hasta la institución educativa?' },
        { id: 'tarifaEstudiantil', tipo: 'opcion', obligatorio: false, opciones: TARIFA_ESTUDIANTIL,
          etiqueta: '¿Paga tarifa estudiantil o pasaje preferencial?' },
      ],
    },
    {
      id: 'dificultades',
      titulo: 'Dificultades',
      campos: [
        { id: 'dificultadPrincipal', ref: 22, tipo: 'opcion', obligatorio: true, opciones: DIFICULTADES,
          etiqueta: '¿Cuál considera que es la principal dificultad de su desplazamiento hacia la institución educativa?' },
        { id: 'dificultadOtro', ref: 22, tipo: 'texto', etiqueta: 'Especifique la dificultad', maxLargo: 80,
          obligatorio: { campo: 'dificultadPrincipal', en: ['otro'] }, visibleSi: { campo: 'dificultadPrincipal', en: ['otro'] } },
        { id: 'dificultadesOtras', tipo: 'multiple', obligatorio: false, max: 3,
          opciones: DIFICULTADES.filter((d) => d.valor !== 'ninguna'),
          etiqueta: '¿Alguna otra dificultad? (hasta 3)', visibleSi: { campo: 'dificultadPrincipal', en: DIFICULTADES.map((d) => d.valor).filter((v) => v !== 'ninguna') } },
        { id: 'observaciones', tipo: 'texto', obligatorio: false, maxLargo: 300, soloAsistida: true, etiqueta: 'Observaciones del encuestador' },
      ],
    },
  ],
}

/** Lee un valor anidado con notación de puntos (`declarados.diasMes`). */
export function leer(obj: unknown, ruta: string): unknown {
  return ruta.split('.').reduce<unknown>((o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj)
}

export function cumple(cond: Condicion | undefined, datos: unknown): boolean {
  if (!cond) return true
  const v = leer(datos, cond.campo)
  let ok = true
  if (cond.en) ok = cond.en.includes(v as never) || (v === undefined && cond.en.includes(false))
  if (ok && cond.menorQue !== undefined) ok = typeof v === 'number' && v < cond.menorQue
  if (ok && cond.y) ok = cond.y.every((c) => cumple(c, datos))
  return ok
}

export function esVisible(campo: Campo, datos: unknown, modoCaptura: 'autoaplicada_qr' | 'asistida'): boolean {
  if (campo.soloAsistida && modoCaptura !== 'asistida') return false
  return cumple(campo.visibleSi, datos)
}

export function esObligatorio(campo: Campo, datos: unknown, modoCaptura: 'autoaplicada_qr' | 'asistida'): boolean {
  if (!esVisible(campo, datos, modoCaptura)) return false
  return typeof campo.obligatorio === 'boolean' ? campo.obligatorio : cumple(campo.obligatorio, datos)
}
